import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X, FileText } from "lucide-react";
import { api, fileUrl, formatApiError, uploadFile } from "@/lib/api";
import { CATEGORY_LABELS, CATEGORY_KEY_TO_SLUG, formatMXN } from "@/lib/catalog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { UnitStatusBadge } from "@/components/catalog/UnitStatusBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OfertaEditor, OfertaBadge, ofertaFromItem, ofertaToPayload } from "@/components/admin/OfertaEditor";
import { Switch } from "@/components/ui/switch";

const TRANSMISIONES = ["Automática", "Manual", "Otra"];
const FORMATOS = [["auto", "Automático"], ["horizontal", "Horizontal"], ["vertical", "Vertical"]];
const PDF_MAX_MB = 10;

const ESTADOS = ["Disponible", "Apartado", "Próximamente"];

const EMPTY = {
  titulo: "", marca: "", modelo: "", anio: "", version: "", precio: "",
  imagen_principal: null, galeria: [], youtube_url: "", estado: "Disponible", descripcion: "",
  agencia_aliada_id: "none", vigencia_inicio: "", vigencia_fin: "",
  kilometraje: "", transmision: "none", ficha_pdf: null, video_visible: false, video_formato: "auto",
};

export default function CatalogUnits({ category = "seminuevo", moduleNumber = "07" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [agencies, setAgencies] = useState([]);

  const isPromo = category === "promocion";
  const isNuevo = category === "nuevo";

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/catalog/units?category=${category}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [category]);
  useEffect(() => {
    if (isNuevo) api.get("/partners/agencies").then((r) => setAgencies(r.data)).catch(() => {});
  }, [isNuevo]);

  const filtered = useMemo(() => items.filter((it) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return [it.titulo, it.marca, it.modelo, String(it.anio)].some((v) => String(v || "").toLowerCase().includes(t));
  }), [items, q]);

  async function onDelete(id) {
    if (!window.confirm("¿Eliminar esta unidad?")) return;
    try { await api.delete(`/catalog/units/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  const label = CATEGORY_LABELS[category] || category;
  const publicSlug = CATEGORY_KEY_TO_SLUG[category] || category;

  return (
    <div data-testid={`admin-catalog-${category}`}>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Módulo {moduleNumber}</p>
          <h1 className="mt-1 font-serif text-3xl text-[#3E5641] md:text-4xl">
            {isPromo ? "Promociones" : `Inventario: ${label}`}
          </h1>
          <p className="mt-1 text-sm text-[#6B7678]">Se publica en /inventario/{publicSlug}.</p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary" data-testid="cat-new-btn">
          <Plus size={16} /> {isPromo ? "Nueva promoción" : "Nueva unidad"}
        </button>
      </header>

      <Input placeholder="Buscar…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 max-w-sm" data-testid="cat-search" />

      <AsyncListShell loading={loading} isEmpty={filtered.length === 0} loadingTestId="cat-loading" emptyTestId="cat-empty" emptyLabel={isPromo ? "Sin promociones aún." : "Sin unidades aún."}>
        <div className="overflow-hidden rounded-sm border border-[#3E5641]/10 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
                <tr>
                  <th className="px-4 py-3">{isPromo ? "Promoción" : "Unidad"}</th>
                  {!isPromo && <th className="px-4 py-3">Precio</th>}
                  {!isPromo && <th className="px-4 py-3">Oferta</th>}
                  <th className="px-4 py-3">Galería</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3E5641]/10">
                {filtered.map((it) => (
                  <tr key={it.id} className="hover:bg-[#F1F3F3]/50" data-testid={`cat-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {it.imagen_principal && <img src={fileUrl(it.imagen_principal)} alt="" className="h-12 w-16 object-cover border border-[#3E5641]/10" />}
                        <div>
                          <div className="font-medium text-[#3E5641]">{it.titulo}</div>
                          {!isPromo && <div className="text-xs text-[#6B7678]">{it.marca} {it.modelo} · {it.anio}</div>}
                        </div>
                      </div>
                    </td>
                    {!isPromo && <td className="px-4 py-3 mono">{formatMXN(it.precio)}</td>}
                    {!isPromo && <td className="px-4 py-3"><OfertaBadge estado={it.oferta_estado} testId={`cat-oferta-${it.id}`} /></td>}
                    <td className="px-4 py-3 mono text-xs">{it.galeria?.length || 0}</td>
                    <td className="px-4 py-3"><UnitStatusBadge estado={it.estado} /></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(it)} className="mr-2 rounded-sm p-1 hover:bg-[#F1F3F3]" data-testid={`cat-edit-${it.id}`}><Pencil size={14} /></button>
                      <button onClick={() => onDelete(it.id)} className="rounded-sm p-1 text-red-600 hover:bg-red-50" data-testid={`cat-delete-${it.id}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AsyncListShell>

      {editing && (
        <UnitDialog
          key={editing === "new" ? "new" : editing.id}
          item={editing === "new" ? null : editing}
          category={category}
          agencies={agencies}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function UnitDialog({ item, category, agencies, onClose, onSaved }) {
  const isNew = !item;
  const isPromo = category === "promocion";
  const isNuevo = category === "nuevo";

  const [form, setForm] = useState({
    ...EMPTY,
    ...(item || {}),
    precio: item?.precio != null ? String(item.precio) : "",
    anio: item?.anio != null ? String(item.anio) : "",
    youtube_url: item?.youtube_url || "",
    galeria: item?.galeria || [],
    descripcion: item?.descripcion || "",
    agencia_aliada_id: item?.agencia_aliada_id || "none",
    vigencia_inicio: item?.vigencia_inicio || "",
    vigencia_fin: item?.vigencia_fin || "",
    kilometraje: item?.kilometraje != null ? String(item.kilometraje) : "",
    transmision: item?.transmision || "none",
    ficha_pdf: item?.ficha_pdf || null,
    video_visible: item ? !!item.video_visible : false,
    video_formato: item?.video_formato || "auto",
  });
  const [videoLabel, setVideoLabel] = useState(item?.video_platform === "tiktok" ? "TikTok" : item?.video_platform === "youtube" ? "YouTube" : "");
  const [videoError, setVideoError] = useState("");
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const videoReq = useRef(0);

  async function checkVideo(url) {
    const n = ++videoReq.current;
    setVideoError("");
    if (!url.trim()) { setVideoLabel(""); return true; }
    try {
      const { data } = await api.post("/admin/video/parse", { url });
      if (n === videoReq.current) setVideoLabel(data.label);
      return true;
    } catch (e) {
      if (n === videoReq.current) { setVideoLabel(""); setVideoError(formatApiError(e)); }
      return false;
    }
  }

  async function onPdf(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf") || (file.type && file.type !== "application/pdf")) { toast.error("Solo se aceptan archivos PDF (.pdf)."); return; }
    if (file.size > PDF_MAX_MB * 1024 * 1024) { toast.error(`El PDF pesa ${(file.size / 1048576).toFixed(1)} MB. El máximo es ${PDF_MAX_MB} MB.`); return; }
    setUploadingPdf(true);
    try { setF("ficha_pdf", await uploadFile(file, `catalog/${category}/fichas`)); toast.success("Ficha cargada. Guarda los cambios para publicarla."); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingPdf(false); }
  }
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGal, setUploadingGal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [oferta, setOferta] = useState(() => ofertaFromItem(item?.oferta));
  const [uploadingOferta, setUploadingOferta] = useState(false);
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onMain(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMain(true);
    try { setF("imagen_principal", await uploadFile(file, `catalog/${category}`)); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingMain(false); e.target.value = ""; }
  }

  async function onGallery(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingGal(true);
    try {
      const paths = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) continue;
        paths.push(await uploadFile(file, `catalog/${category}`));
      }
      setForm((f) => ({ ...f, galeria: [...f.galeria, ...paths].slice(0, 15) }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingGal(false); e.target.value = ""; }
  }

  function removeGallery(i) {
    setForm((f) => ({ ...f, galeria: f.galeria.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    if (!form.imagen_principal) { toast.error("Sube la imagen principal"); return; }
    if (!isPromo && (form.galeria.length < 10 || form.galeria.length > 15)) {
      toast.error("La galería debe tener entre 10 y 15 fotos"); return;
    }
    if (isPromo && form.galeria.length > 15) {
      toast.error("La galería no puede tener más de 15 fotos"); return;
    }
    if (!isPromo && form.youtube_url.trim() && !(await checkVideo(form.youtube_url))) {
      toast.error("El link de video no es válido"); return;
    }
    setSaving(true);
    try {
      const payload = {
        category,
        titulo: form.titulo.trim(),
        imagen_principal: form.imagen_principal,
        galeria: form.galeria,
        estado: form.estado,
        youtube_url: form.youtube_url?.trim() || null,
      };
      if (isPromo || isNew) payload.descripcion = form.descripcion.trim();
      if (!isPromo) {
        payload.kilometraje = form.kilometraje === "" ? null : Number(form.kilometraje);
        payload.transmision = form.transmision === "none" ? null : form.transmision;
        payload.ficha_pdf = form.ficha_pdf || null;
        payload.video_visible = !!form.video_visible;
        payload.video_formato = form.video_formato;
        payload.marca = form.marca.trim();
        payload.modelo = form.modelo.trim();
        payload.anio = Number(form.anio);
        payload.version = form.version.trim();
        payload.precio = Number(form.precio);
        if (oferta.vigencia_inicio && oferta.vigencia_fin && oferta.vigencia_fin < oferta.vigencia_inicio) {
          toast.error("Oferta: la fecha de fin no puede ser anterior a la de inicio"); setSaving(false); return;
        }
        payload.oferta = ofertaToPayload(oferta);
      }
      if (isNuevo) {
        payload.agencia_aliada_id = form.agencia_aliada_id === "none" ? null : form.agencia_aliada_id;
      }
      if (isPromo) {
        payload.vigencia_inicio = form.vigencia_inicio || null;
        payload.vigencia_fin = form.vigencia_fin || null;
      }
      if (isNew) await api.post("/catalog/units", payload);
      else await api.patch(`/catalog/units/${item.id}`, payload);
      toast.success(isNew ? "Creada" : "Actualizada");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isNew ? (isPromo ? "Nueva promoción" : "Nueva unidad") : form.titulo}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-2">
          <F label={isPromo ? "Título de la promoción *" : "Título / nombre *"}>
            <Input required value={form.titulo} onChange={(e) => setF("titulo", e.target.value)} data-testid="cat-titulo" />
          </F>
          <div>
            <Label className="field-label">Estado *</Label>
            <Select value={form.estado} onValueChange={(v) => setF("estado", v)}>
              <SelectTrigger data-testid="cat-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {!isPromo && (
            <>
              <F label="Marca *"><Input required value={form.marca} onChange={(e) => setF("marca", e.target.value)} data-testid="cat-marca" /></F>
              <F label="Modelo *"><Input required value={form.modelo} onChange={(e) => setF("modelo", e.target.value)} data-testid="cat-modelo" /></F>
              <F label="Año *"><Input type="number" required value={form.anio} onChange={(e) => setF("anio", e.target.value)} data-testid="cat-anio" /></F>
              <F label="Versión *"><Input required value={form.version} onChange={(e) => setF("version", e.target.value)} data-testid="cat-version" /></F>
              <F label="Precio (MXN) *"><Input type="number" required value={form.precio} onChange={(e) => setF("precio", e.target.value)} data-testid="cat-precio" /></F>
              <F label="Kilometraje (opcional)"><Input type="number" min="0" step="1" value={form.kilometraje} onChange={(e) => setF("kilometraje", e.target.value.replace(/[^\d]/g, ""))} placeholder="Ej. 92500" data-testid="cat-kilometraje" /></F>
              <div>
                <Label className="field-label">Transmisión (opcional)</Label>
                <Select value={form.transmision} onValueChange={(v) => setF("transmision", v)}>
                  <SelectTrigger data-testid="cat-transmision"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin especificar —</SelectItem>
                    {TRANSMISIONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {isNuevo && (
            <div>
              <Label className="field-label">Agencia aliada</Label>
              <Select value={form.agencia_aliada_id} onValueChange={(v) => setF("agencia_aliada_id", v)}>
                <SelectTrigger data-testid="cat-agencia"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin asignar —</SelectItem>
                  {agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
              {agencies.length === 0 && <p className="field-hint">Da de alta agencias en "Socios comerciales" con categoría "Agencias de autos nuevos".</p>}
            </div>
          )}

          {isPromo && (
            <>
              <F label="Vigencia inicio (opcional)"><Input type="date" value={form.vigencia_inicio} onChange={(e) => setF("vigencia_inicio", e.target.value)} data-testid="cat-vig-inicio" /></F>
              <F label="Vigencia fin (opcional)"><Input type="date" value={form.vigencia_fin} onChange={(e) => setF("vigencia_fin", e.target.value)} data-testid="cat-vig-fin" /></F>
            </>
          )}

          {isPromo && (
            <F label="Link de YouTube (opcional)">
              <Input value={form.youtube_url} onChange={(e) => setF("youtube_url", e.target.value)} placeholder="https://youtu.be/…" data-testid="cat-youtube" />
            </F>
          )}
        </div>

        {!isPromo && (
          <div className="grid gap-4 rounded-sm border border-[#3E5641]/10 p-4 md:grid-cols-2" data-testid="cat-video-block">
            <div className="md:col-span-2">
              <Label className="field-label">Link de video (YouTube o TikTok, opcional)</Label>
              <div className="flex items-center gap-2">
                <Input value={form.youtube_url} onChange={(e) => { videoReq.current += 1; setF("youtube_url", e.target.value); setVideoLabel(""); setVideoError(""); }}
                  onBlur={(e) => checkVideo(e.target.value)} placeholder="https://youtu.be/… o https://vm.tiktok.com/…" data-testid="cat-youtube" />
                {videoLabel && <span className="shrink-0 rounded-sm bg-green-50 px-2 py-1 text-xs text-green-800 border border-green-200" data-testid="cat-video-platform">{videoLabel}</span>}
              </div>
              {videoError && <p className="mt-1 text-xs text-red-600" data-testid="cat-video-error">{videoError}</p>}
            </div>
            <label className="flex items-center justify-between gap-3 text-sm text-[#3E5641]">
              Mostrar botón de Video
              <Switch checked={form.video_visible} onCheckedChange={(v) => setF("video_visible", v)} data-testid="cat-video-visible" />
            </label>
            <div>
              <Label className="field-label">Formato del video</Label>
              <Select value={form.video_formato} onValueChange={(v) => setF("video_formato", v)}>
                <SelectTrigger data-testid="cat-video-formato"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMATOS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <p className="field-hint">Automático: TikTok y Shorts se ven verticales; el resto de YouTube, horizontal.</p>
            </div>
          </div>
        )}

        {!isPromo && (
          <div data-testid="cat-pdf-block">
            <Label className="field-label">Ficha técnica (PDF, máximo {PDF_MAX_MB} MB)</Label>
            <div className="flex flex-wrap items-center gap-3">
              {form.ficha_pdf && (
                <a href={fileUrl(form.ficha_pdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-[#3E5641] underline" data-testid="cat-pdf-current">
                  <FileText size={14} /> Ver ficha cargada
                </a>
              )}
              <label className="inline-flex cursor-pointer items-center gap-2 border border-dashed border-[#3E5641] px-4 py-3 text-sm">
                <Upload size={14} /> {uploadingPdf ? "Subiendo..." : form.ficha_pdf ? "Reemplazar PDF" : "Subir PDF"}
                <input type="file" hidden accept="application/pdf,.pdf" onChange={onPdf} data-testid="cat-pdf-input" />
              </label>
              {form.ficha_pdf && (
                <button type="button" onClick={() => setF("ficha_pdf", null)} className="text-xs text-red-600 underline" data-testid="cat-pdf-remove">Eliminar ficha</button>
              )}
            </div>
          </div>
        )}

        <div>
          <Label className="field-label">Imagen principal *</Label>
          <div className="flex items-center gap-4">
            {form.imagen_principal && <img src={fileUrl(form.imagen_principal)} alt="" className="h-24 w-32 object-cover border border-[#3E5641]/10" data-testid="cat-main-preview" />}
            <label className="inline-flex cursor-pointer items-center gap-2 border border-dashed border-[#3E5641] px-4 py-3 text-sm">
              <Upload size={14} /> {uploadingMain ? "Subiendo..." : (form.imagen_principal ? "Cambiar" : "Subir imagen principal")}
              <input type="file" hidden accept="image/*" onChange={onMain} data-testid="cat-main-input" />
            </label>
          </div>
        </div>

        <div>
          <Label className="field-label">
            {isPromo ? "Galería opcional (hasta 15 fotos)" : "Galería adicional (10 a 15 fotos)"}
          </Label>
          <label className="flex cursor-pointer items-center gap-2 border border-dashed border-[#3E5641] px-4 py-3 text-sm">
            <Upload size={14} /> {uploadingGal ? "Subiendo..." : "Agregar fotos"}
            <input type="file" hidden multiple accept="image/*" onChange={onGallery} data-testid="cat-gallery-input" />
          </label>
          <p className="mt-2 mono text-xs text-[#6B7678]" data-testid="cat-gallery-count">
            {form.galeria.length} / {isPromo ? "0–15" : "10–15"}
          </p>
          {form.galeria.length > 0 && (
            <div className="mt-3 grid grid-cols-5 gap-2 md:grid-cols-8">
              {form.galeria.map((p, i) => (
                <div key={p + i} className="relative">
                  <img src={fileUrl(p)} alt="" className="aspect-square w-full object-cover border border-[#3E5641]/10" />
                  <button type="button" onClick={() => removeGallery(i)} className="absolute right-1 top-1 rounded-sm bg-white/95 p-1 text-[#3E5641] hover:bg-white" aria-label="Quitar">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {isPromo ? (
          <div>
            <Label className="field-label">Descripción *</Label>
            <Textarea required rows={5} value={form.descripcion} onChange={(e) => setF("descripcion", e.target.value)} data-testid="cat-descripcion" />
          </div>
        ) : item?.descripcion ? (
          <details className="rounded-sm border border-[#3E5641]/10 p-3" data-testid="cat-descripcion-anterior">
            <summary className="cursor-pointer text-sm text-[#6B7678]">Descripción anterior (ya no se muestra en el sitio)</summary>
            <p className="mt-2 whitespace-pre-line text-sm text-[#3E5641]" data-testid="cat-descripcion-anterior-texto">{item.descripcion}</p>
          </details>
        ) : null}

        {!isPromo && (
          <OfertaEditor value={oferta} onChange={setOferta} estado={item?.oferta_estado} category={category}
            unitPrice={form.precio} onUploading={setUploadingOferta} />
        )}

        <DialogFooter>
          <button onClick={onClose} className="btn-outline-dark">Cancelar</button>
          <button onClick={save} disabled={saving || uploadingMain || uploadingGal || uploadingOferta || uploadingPdf} className="btn-primary disabled:opacity-60" data-testid="cat-save">
            {saving ? "Guardando..." : (isNew ? "Crear" : "Guardar cambios")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) { return <div><Label className="field-label">{label}</Label>{children}</div>; }
